/**
 * Navbar Component Documentation
 *
 * This component serves as the main navigation bar for the Silanga application.
 * It provides responsive navigation across different screen sizes and includes:
 * - Logo/Brand name
 * - Navigation links
 * - Language selector
 * - Mobile-responsive menu
 * - Authentication state handling
 *
 * Key Features:
 * 1. Responsive Design: Adapts to mobile and desktop views
 * 2. Internationalization: Supports multiple languages
 * 3. Authentication Integration: Handles login/logout states
 * 4. Dynamic Navigation: Loads navigation items from configuration
 *
 * Usage:
 * This component should be placed at the top of the application layout
 * and will automatically handle navigation and user authentication states.
 */

import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "../../context/TranslationContext";
import LanguageSelector from "./LanguageSelector";
import { pages } from "../../pages/StaticPages/pagesConfig";
import { useAuth } from "../Auth/AuthContext";
import Cookies from "js-cookie";

/**
 * Navbar Component
 *
 * @returns {JSX.Element} The rendered navigation bar component
 */
function Navbar() {
  // State management for mobile menu and logout dialog
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Hooks for navigation, translation, and authentication
  const navigate = useNavigate();
  const { currentLanguage, setCurrentLanguage, translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isLoggedIn, logout } = useAuth();
  const location = useLocation();

  /**
   * Toggles the mobile menu drawer
   */
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  /**
   * Handles the logout button click
   * Opens the logout confirmation dialog
   */
  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    setMobileOpen(false);
  };

  /**
   * Handles the confirmation of logout
   * - Performs logout action
   * - Removes access token from cookies
   * - Closes the dialog
   * - Navigates to home page
   */
  const handleLogoutConfirm = () => {
    logout();
    Cookies.remove("accessToken");
    setLogoutDialogOpen(false);
    navigate("/");
  };

  /**
   * Handles the cancellation of logout
   * Closes the logout confirmation dialog
   */
  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  /**
   * Static navigation items configuration
   * Combines home page with dynamic pages from pagesConfig
   * Filters and sorts pages based on configuration
   */
  const staticNavItems = [
    {
      title: "Home",
      path: "/",
    },
    ...Object.entries(pages)
      .filter(([_, page]) => page.showInNav)
      .sort((a, b) => (a[1].navOrder || 0) - (b[1].navOrder || 0))
      .map(([key, page]) => ({
        title: page.title,
        path: page.path,
      })),
  ];

  /**
   * Mobile drawer component
   * Contains navigation links and authentication options
   */
  const drawer = (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 2,
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            "&:hover": {
              bgcolor: "transparent",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ flexGrow: 1, pt: 4 }}>
        {staticNavItems.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            disableRipple
            sx={{
              textAlign: "center",
              py: 2,
              color: "inherit",
              fontWeight: location.pathname === item.path ? 700 : 400,
              textDecoration:
                location.pathname === item.path ? "underline" : "none",
              "&:hover": {
                bgcolor: "transparent",
                color: "black",
              },
              "&:active": {
                color: "inherit",
              },
              transition: "none",
            }}
          >
            <ListItemText
              primary={translate(item.title)}
              primaryTypographyProps={{
                sx: {
                  fontWeight: "inherit",
                  textDecoration: "inherit",
                  color: "inherit",
                },
              }}
              disableTypography={false}
            />
          </ListItem>
        ))}
        {/* <Divider sx={{ my: 2 }} />
        {isLoggedIn ? (
          <ListItem 
            onClick={handleLogoutClick}
            sx={{
              textAlign: 'center',
              py: 2,
              cursor: 'pointer'
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary={translate('Logout')} />
          </ListItem>
        ) : (
          <ListItem 
            component={Link}
            to="/auth"
            onClick={handleDrawerToggle}
            sx={{
              textAlign: 'center',
              py: 2
            }}
          >
            <ListItemText primary={translate('Login / Sign Up')} />
          </ListItem>
        )} */}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed">
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            <Link
              to="/"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "inline-block",
              }}
            >
              <Typography variant="h6">{translate("Silanga")}</Typography>
            </Link>
          </Box>

          <LanguageSelector
            currentLanguage={currentLanguage}
            setCurrentLanguage={setCurrentLanguage}
          />

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%", // Ensure full height
              }}
            >
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mx: 2,
                  height: "24px",
                  my: "auto", // Center vertically
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  alignSelf: "center", // Ensure center alignment
                }}
              />

              {staticNavItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  sx={{
                    ml: 2,
                    fontWeight: location.pathname === item.path ? 700 : 400,
                    textDecoration:
                      location.pathname === item.path ? "underline" : "none",
                    "&:hover": {
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {translate(item.title)}
                </Button>
              ))}
              {/* {isLoggedIn ? (
                <Button
                  color="inherit"
                  onClick={handleLogoutClick}
                  endIcon={<ExitToAppIcon />}
                  sx={{ ml: 2 }}
                >
                  {translate('Logout')}
                </Button>
              ) : (
                <Button
                  color="inherit"
                  component={Link}
                  to="/auth"
                  endIcon={<AccountCircleIcon />}
                  sx={{ ml: 2 }}
                >
                  {translate('Login / Sign Up')}
                </Button>
              )} */}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: { width: "100%" },
        }}
      >
        {drawer}
      </Drawer>

      {/* <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
      >
        <DialogTitle>{translate("Logout")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate("Are you sure you want to log out?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary">
            {translate("Cancel")}
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            color="primary"
            sx={{
              border: "1px solid black",
            }}
            autoFocus
          >
            {translate("Logout")}
          </Button>
        </DialogActions>
      </Dialog> */}
    </>
  );
}

export default Navbar;
